import { Controller } from "@hotwired/stimulus"
import SignaturePad from "signature_pad"

// Browsers cannot draw these; the server converts them (Signatures::ImageNormalizer).
const NO_PREVIEW_TYPES = ["image/heic", "image/heif"]

// A signature drawn on the pad, or — where the report type allows it — a picture of one uploaded
// instead. Either way the input carries a data URL; the upload flag tells the server which, so it
// can clean up the picture and record that it was uploaded. Drawing replaces an upload and vice
// versa.
//
// Optional targets, for a field that already holds a signature: `existing` shows the stored one
// (drawing or uploading replaces it), `clearInput` tells the server "Löschen" removed it.
export default class extends Controller {
  static targets = [
    "canvas",
    "input",
    "existing",
    "clearInput",
    "uploadInput",
    "uploadFlag",
    "uploadPreview",
    "uploadImage",
    "uploadNotice",
  ]

  static values = {
    uploadOn: { type: String, default: "1" },
    uploadOff: { type: String, default: "0" },
  }

  connect() {
    const canvas = this.canvasTarget

    if (canvas) {
      // Set canvas dimensions to match display size
      this.resizeCanvas()

      // Initialize SignaturePad
      this.signaturePad = new SignaturePad(canvas, {
        penColor: "rgb(66, 133, 244)",
        backgroundColor: "rgb(255, 255, 255)",
      })
      this._forceIOSTouchEvents()

      // A stroke replaces an uploaded picture and the stored signature
      this.signaturePad.addEventListener("beginStroke", () => this.startDrawing())

      // Save signature data to hidden input on stroke end
      this.signaturePad.addEventListener("endStroke", () => {
        this.inputTarget.value = this.signaturePad.toDataURL()
      })

      // A failed save re-renders an uploaded picture as one, not drawn onto the pad
      if (this.inputTarget.value && this.uploaded) {
        this.showUpload(this.inputTarget.value, null)
      } else if (this.inputTarget.value) {
        this.loadSignature(this.inputTarget.value)
      }

      // Handle window resize
      this.boundResizeCanvas = this.resizeCanvas.bind(this)
      window.addEventListener("resize", this.boundResizeCanvas)
    }
  }

  disconnect() {
    if (this.signaturePad) {
      this.signaturePad.off()
    }
    if (this.boundResizeCanvas) {
      window.removeEventListener("resize", this.boundResizeCanvas)
    }
  }

  clear() {
    if (this.signaturePad) {
      this.signaturePad.clear()
      this.inputTarget.value = ""
    }
    this.hideUpload()
    this.removeExisting()
  }

  // Undo last stroke
  undo() {
    if (this.signaturePad) {
      const data = this.signaturePad.toData()
      if (data.length > 0) {
        data.pop()
        this.signaturePad.clear()
        this.signaturePad.fromData(data)
        this.inputTarget.value = data.length > 0 ? this.signaturePad.toDataURL() : ""
      }
    }
  }

  pickUpload() {
    if (this.hasUploadInputTarget) this.uploadInputTarget.click()
  }

  upload(event) {
    const file = event.target.files?.[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = () => {
      this.signaturePad?.clear()
      this.inputTarget.value = reader.result
      this.showUpload(reader.result, file.type)
      this.hideExisting()
    }
    reader.readAsDataURL(file)
    event.target.value = ""
  }

  startDrawing() {
    if (this.uploaded) {
      this.inputTarget.value = ""
      this.hideUpload()
    }
    this.hideExisting()
  }

  get uploaded() {
    return this.hasUploadFlagTarget && this.uploadFlagTarget.value === this.uploadOnValue
  }

  showUpload(dataUrl, type) {
    if (this.hasUploadFlagTarget) this.uploadFlagTarget.value = this.uploadOnValue
    if (!this.hasUploadPreviewTarget) return

    const previewable = !NO_PREVIEW_TYPES.includes(type || dataUrl.slice(5, dataUrl.indexOf(";")))
    if (this.hasUploadImageTarget) {
      this.uploadImageTarget.src = previewable ? dataUrl : ""
      this.uploadImageTarget.classList.toggle("hidden", !previewable)
    }
    if (this.hasUploadNoticeTarget) this.uploadNoticeTarget.classList.toggle("hidden", previewable)
    this.uploadPreviewTarget.classList.remove("hidden")
  }

  hideUpload() {
    if (this.hasUploadFlagTarget) this.uploadFlagTarget.value = this.uploadOffValue
    if (this.hasUploadPreviewTarget) this.uploadPreviewTarget.classList.add("hidden")
    if (this.hasUploadImageTarget) this.uploadImageTarget.src = ""
  }

  hideExisting() {
    if (this.hasExistingTarget) this.existingTarget.classList.add("hidden")
  }

  // "Löschen" with a stored signature: it goes on save, unless a new one replaces it first.
  removeExisting() {
    if (!this.hasExistingTarget || this.existingTarget.classList.contains("hidden")) return

    this.hideExisting()
    if (this.hasClearInputTarget) this.clearInputTarget.value = "1"
  }

  resizeCanvas() {
    const canvas = this.canvasTarget
    const ratio = Math.max(window.devicePixelRatio || 1, 1)

    // Save current signature data before resizing
    const data = this.signaturePad?.toData()

    // Set canvas size
    canvas.width = canvas.offsetWidth * ratio
    canvas.height = canvas.offsetHeight * ratio

    // Reset transform and apply fresh scale (important: scale is cumulative!)
    const ctx = canvas.getContext("2d")
    ctx.setTransform(1, 0, 0, 1, 0, 0) // Reset to identity matrix
    ctx.scale(ratio, ratio)

    // Restore signature data after resize
    if (this.signaturePad && data && data.length > 0) {
      this.signaturePad.fromData(data)
    }
  }

  _forceIOSTouchEvents() {
    const isIOS =
      /iPhone|iPad|iPod/.test(navigator.userAgent) ||
      (/Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1)
    if (isIOS && window.PointerEvent) {
      this.signaturePad.off()
      const origPointerEvent = window.PointerEvent
      window.PointerEvent = undefined
      this.signaturePad.on()
      window.PointerEvent = origPointerEvent
    }
  }

  loadSignature(dataUrl) {
    if (this.signaturePad && dataUrl) {
      // Clear first
      this.signaturePad.clear()

      // Load the image
      const img = new Image()
      img.onload = () => {
        const canvas = this.canvasTarget
        const ctx = canvas.getContext("2d")

        // Use CSS dimensions (offsetWidth/offsetHeight) instead of scaled pixel dimensions
        // The canvas context already has device pixel ratio scaling applied via ctx.scale()
        const displayWidth = canvas.offsetWidth
        const displayHeight = canvas.offsetHeight

        // Calculate scaling to fit the image in the display area
        const scale = Math.min(displayWidth / img.width, displayHeight / img.height)

        // Center the image within the display area
        const x = (displayWidth - img.width * scale) / 2
        const y = (displayHeight - img.height * scale) / 2

        // Draw at CSS coordinates - the context scale handles pixel ratio conversion
        ctx.drawImage(img, x, y, img.width * scale, img.height * scale)
      }
      img.src = dataUrl
    }
  }
}
